import React, { useContext, useEffect, useState, useCallback } from 'react';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import { Card, DonutChartComponent, BarChartComponent } from 'src/components';
import { TransactionFilterContext } from 'src/context/transaction-filter-context';
import { getTwelveAMOfToday } from 'src/helpers/dates';
import { formatCurrencyAmountSimple } from 'src/helpers/formatting';
import { filterDashboard } from 'src/services/payment-dashboard.service';
import TechnicalErrorSelfService from '../../technical-error-self-service/technical-error-self-service';
import { queue } from 'src/helpers/delay';

import './filtered-dashboard.scss';

let BarChartAxis = {
  x: {
    key: 'time',
    displayName: 'Date',
    minValue: null,
    maxValue: null,
    displayFormat: 'dd LLL',
    toolTipDisplayFormat: 'dd LLL yyyy',
  },
  y: {
    key: 'numTransactions',
    displayName: '',
  },
};

const customTooltip = (props, key) => {
  if (props?.payload.length <= 0 || key === null) {
    return null;
  }
  const index = props.payload.findIndex((bars) => bars.dataKey === key);
  if (key !== 'all' && index === -1) {
    return null;
  }
  const hovered = props.payload[index];

  return (
    <div className="card custom-tooltip has-padding-6">
      <div>
        <div className="tooltip-date">
          {DateTime.fromMillis(props.label).toFormat(BarChartAxis.x.toolTipDisplayFormat)}
        </div>
        <div>
          Sales
          <span className="has-text-weight-bold has-text-right is-pulled-right has-padding-left-7">
            {hovered.payload[hovered.dataKey]}
          </span>
        </div>
        <div>
          Total
          <span className="has-text-weight-bold has-text-right is-pulled-right has-padding-left-7">
            R {formatCurrencyAmountSimple(hovered.payload[`amount-${hovered.dataKey}`])}
          </span>
        </div>
      </div>
    </div>
  );
};

function FilteredDashboard(props) {
  const today = getTwelveAMOfToday();
  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);

  const { transactionFilter } = useContext(TransactionFilterContext);

  const [totalSales, setTotalSales] = useState(formatCurrencyAmountSimple(0));
  const [totalTransactions, setTotalTransactions] = useState(formatCurrencyAmountSimple(0));
  const [transactionsData, setTransactionsData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [dailyLines, setDailyLines] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const setTerminalOption = props.setTerminalOption;
  const filterByTerminal = props.filterByTerminal;

  const [transactionsDataFailure, setTransactionsDataFailure] = useState(false);

  const terminalFilterOptionsSet = useCallback((option) => {
    if (setTerminalOption) {
      setTerminalOption(option);
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    const processDataForGraphs = (transactionSummary, transactionTotals) => {
      let sale = [],
        transaction = [],
        intervals = [],
        terminalOptions = [],
        bars = [];

      Object.keys(transactionSummary).forEach((terminalId) => {
        terminalOptions.push({
          type: transactionTotals.perTerminalTotals[terminalId].terminalType,
          id: terminalId,
        });

        if (
          !filterByTerminal ||
          !filterByTerminal.type ||
          filterByTerminal.type === transactionTotals.perTerminalTotals[terminalId].terminalType
        ) {
          sale.push({
            name: transactionTotals.perTerminalTotals[terminalId].terminalType,
            value: transactionTotals.perTerminalTotals[terminalId].transactionCreditAmount,
          });
          transaction.push({
            name: transactionTotals.perTerminalTotals[terminalId].terminalType,
            value: transactionTotals.perTerminalTotals[terminalId].numberOfTransactions,
          });
          bars.push({ name: transactionSummary[terminalId].terminalType, key: terminalId });
        }
      });

      const fromDate = DateTime.fromJSDate(transactionFilter.selectedDates[0]);
      const toDate = DateTime.fromJSDate(transactionFilter.selectedDates[1]);
      let duration = toDate.diff(fromDate, 'days').toObject();
      let intervalSelection = 'daily';
      let intervalIncrement = 'days';
      let intervalFormat = 'yyyy-MM-dd';
      BarChartAxis.x.displayFormat = 'dd LLL';
      BarChartAxis.x.toolTipDisplayFormat = 'dd LLL yyyy';
      if (duration.days <= 1) {
        duration = toDate.diff(fromDate, 'hours').toObject();
        intervalSelection = 'hourly';
        intervalIncrement = 'hours';
        intervalFormat = 'H:mm';
        BarChartAxis.x.displayName = 'Hours';
        BarChartAxis.x.displayFormat = 'H:mm';
        BarChartAxis.x.toolTipDisplayFormat = 'dd LLL, H:mm';
      }
      for (let i = 0; i < duration[intervalIncrement]; i++) {
        const date =
          intervalSelection !== 'hourly'
            ? fromDate.plus({ [intervalIncrement]: i }).toFormat(intervalFormat)
            : `${fromDate.plus({ [intervalIncrement]: i }).toFormat(intervalFormat)}-${fromDate
                .plus({ [intervalIncrement]: i + 1 })
                .toFormat(intervalFormat)}`;

        let terminalValuePair = {};
        Object.keys(transactionSummary).forEach((terminalId) => {
          if (
            !filterByTerminal ||
            !filterByTerminal.type ||
            filterByTerminal.type === transactionSummary[terminalId].terminalType
          ) {
            terminalValuePair[terminalId] =
              transactionSummary[terminalId][intervalSelection][date]?.numberOfTransactions || 0;
            terminalValuePair[`amount-${terminalId}`] =
              transactionSummary[terminalId][intervalSelection][date]?.transactionNetAmount || 0;
          }
        });

        const currentInterval = fromDate.plus({ [intervalIncrement]: i });
        intervals.push({
          name: currentInterval.toMillis(),
          ...terminalValuePair,
        });
      }

      if (!abortController.signal.aborted) {
        terminalFilterOptionsSet(terminalOptions);
        setSalesData(sale);
        setTransactionsData(transaction);
        setDailyData(intervals);
        setDailyLines(bars);
      }
    };

    const filterDashboardGraphs = async () => {
      setIsLoading(true);
      const fromDate = DateTime.fromJSDate(transactionFilter.selectedDates[0]);
      let toDate = DateTime.fromJSDate(transactionFilter.selectedDates[1]);
      let duration = toDate.diff(fromDate, 'days').toObject();
      if (duration.days <= 1) {
        toDate = toDate.plus(-1);
      }
      BarChartAxis.x.minValue = fromDate.toMillis();
      BarChartAxis.x.maxValue = toDate.toMillis();

      const data = await filterDashboard(
        fromDate.toJSDate(),
        toDate.toJSDate(),
        abortController,
        () => {
          setIsLoading(false);
          setTransactionsDataFailure(true);
          props?.onFailure();
        },
        false,
        true,
      );
      if (!abortController.signal.aborted) {
        setTotalSales(
          data?.datedTransactionsDetails?.transactionTotals?.totalTransactionCreditAmount || 0,
        );
        setTotalTransactions(
          data?.datedTransactionsDetails?.transactionTotals?.totalNumberOfTransactions || 0,
        );
        processDataForGraphs(
          data?.datedTransactionsDetails?.transactionSummary || {},
          data?.datedTransactionsDetails?.transactionTotals || {},
        );
        setIsLoading(false);
      }
    };

    queue.push(filterDashboardGraphs);

    return () => {
      abortController.abort();
    };
  }, [transactionFilter.selectedDates, filterByTerminal, terminalFilterOptionsSet]);

  const donutChartPresent = !isLoading && salesData?.length > 0;

  return (
    <div className="FilteredDashboard" data-testid="FilteredDashboard">
      <div className="donut-charts">
        <Card
          stretchVertical={true}
          className={`donut-chart-card ${donutChartPresent ? 'has-donut-chart-child' : ''}`}
          cardTitle="Total sales"
        >
          {!isLoading || salesData?.length > 0 ? (
            salesData?.length > 0 ? (
              <div automationid={`PaymentDashboard_Div_TotalSalesPieChart`}>
                <DonutChartComponent
                  formatter={(value) => formatCurrencyAmountSimple(value)}
                  className="pie-graph is-hidden-mobile"
                  valuePrefix="R "
                  maxRadius={120}
                  chartData={salesData}
                  chartLabel={`R ${formatCurrencyAmountSimple(totalSales)}`}
                  chartSubLabel={'Total sales'}
                ></DonutChartComponent>
                <DonutChartComponent
                  formatter={(value) => formatCurrencyAmountSimple(value)}
                  className="pie-graph is-hidden-tablet"
                  valuePrefix="R "
                  maxRadius={100}
                  chartData={salesData}
                  chartLabel={`R ${formatCurrencyAmountSimple(totalSales)}`}
                  chartSubLabel={'Total sales'}
                  isMobile={true}
                ></DonutChartComponent>
              </div>
            ) : (
              <div
                automationid={`PaymentDashboard_Div_TotalSalesNoData`}
                className="has-margin-5 is-size-5 has-text-centered"
              >
                No data to display
              </div>
            )
          ) : (
            <div className="load-more has-margin-5 is-size-5 has-text-centered">Loading. . .</div>
          )}
        </Card>
        <Card
          stretchVertical={true}
          className={`donut-chart-card ${donutChartPresent ? 'has-donut-chart-child' : ''}`}
          cardTitle="Total transactions"
        >
          {!isLoading || transactionsData?.length > 0 ? (
            transactionsData?.length > 0 ? (
              <>
                <DonutChartComponent
                  className="pie-graph is-hidden-mobile"
                  chartData={transactionsData}
                  maxRadius={120}
                  chartLabel={`${formatCurrencyAmountSimple(totalTransactions)}`}
                  chartSubLabel={`Sales and Refunds`}
                ></DonutChartComponent>
                <DonutChartComponent
                  className="pie-graph is-hidden-tablet"
                  chartData={transactionsData}
                  maxRadius={100}
                  chartLabel={`${formatCurrencyAmountSimple(totalTransactions)}`}
                  chartSubLabel={`Sales and Refunds`}
                  isMobile={true}
                ></DonutChartComponent>
              </>
            ) : (
              <div
                className="has-margin-5 is-size-5 has-text-centered"
                automationid={`PaymentDashboard_Div_TotalTransactionNoData`}
              >
                No data to display
              </div>
            )
          ) : (
            <div className="load-more has-margin-5 is-size-5 has-text-centered">Loading. . .</div>
          )}
        </Card>
      </div>
      <div className="columns">
        <Card className="column is-full" cardTitle="Daily transactions">
          {transactionsDataFailure ? (
            <TechnicalErrorSelfService manualFailure="transaction-data" hasError />
          ) : !isLoading || salesData?.length > 0 ? (
            salesData?.length > 0 ? (
              <div automationid={`PaymentDashboard_Div_DailyTransactionPieChart`}>
                <BarChartComponent
                  className="is-hidden-mobile"
                  xAxis={BarChartAxis.x}
                  yAxis={BarChartAxis.y}
                  chartData={dailyData}
                  bars={dailyLines}
                  customTooltip={customTooltip}
                  isMobile={false}
                ></BarChartComponent>
                <BarChartComponent
                  className="is-hidden-tablet"
                  xAxis={BarChartAxis.x}
                  yAxis={BarChartAxis.y}
                  chartData={dailyData}
                  bars={dailyLines}
                  customTooltip={customTooltip}
                  isMobile={true}
                ></BarChartComponent>
              </div>
            ) : (
              <div
                automationid={`PaymentDashboard_Div_DailyTransactionNoData`}
                className="is-size-5 has-text-centered"
              >
                No data to display
              </div>
            )
          ) : (
            <div className="load-more is-size-5 has-text-centered">Loading. . .</div>
          )}
        </Card>
      </div>
    </div>
  );
}

FilteredDashboard.propTypes = {
  filterByTerminal: PropTypes.object,
  setTerminalOption: PropTypes.func,
};

FilteredDashboard.defaultProps = {
  filterByTerminal: null,
  setTerminalOption: null,
};

export default FilteredDashboard;
